/**
 * Address-history coverage engine (Tzvi ruling 2026-07-19).
 *
 * The Department needs a COMPLETE 10-year address history: the
 * countries-resided date ranges must tile the whole window with no gaps.
 * This engine detects the holes at month granularity. It powers three layers
 * on the partner forms:
 *   1. upfront copy on the subsection (schema description),
 *   2. a live cross-field warning while the client types,
 *   3. a blocking validation error: the form cannot be submitted (and the
 *      server-side stage latch will not fire) until the history is gapless.
 *
 * Short visits (countries_visited, under 12 months) are deliberately NOT
 * checked: gaps between trips are normal.
 */

export interface CoverageGap {
  /** Inclusive month labels, e.g. "Mar 2019". */
  from: string;
  to: string;
}

export interface CoverageResult {
  complete: boolean;
  gaps: CoverageGap[];
  /** Entries with parseable from/to dates that were considered. */
  usedEntries: number;
}

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/**
 * 'YYYY-MM', 'YYYY-MM-DD' or bare 'YYYY' -> absolute month index; null when
 * unparseable. A year-only value spans the whole year: it resolves to January
 * as a start date and December as an end date.
 */
function monthIndex(v: unknown, edge: 'from' | 'to'): number | null {
  if (typeof v !== 'string') return null;
  const t = v.trim();
  const ym = /^(\d{4})-(\d{2})/.exec(t);
  if (ym) {
    const month = Number(ym[2]);
    if (month < 1 || month > 12) return null;
    return Number(ym[1]) * 12 + (month - 1);
  }
  const y = /^(\d{4})$/.exec(t);
  if (y) return Number(y[1]) * 12 + (edge === 'from' ? 0 : 11);
  return null;
}

function label(idx: number): string {
  return `${MONTHS[idx % 12]} ${Math.floor(idx / 12)}`;
}

export interface HistoryEntry {
  date_from?: unknown;
  date_to?: unknown;
}

/**
 * Analyse coverage of the trailing `windowYears` window ending this month.
 * Ranges are clamped to the window; overlapping or back-to-back ranges merge
 * (a range starting the month after another ends is continuous). Unparseable
 * entries are skipped — their own field validation handles them.
 */
export function analyseAddressHistoryCoverage(
  entries: unknown,
  today: Date = new Date(),
  windowYears = 10,
): CoverageResult {
  const windowEnd = today.getFullYear() * 12 + today.getMonth();
  const windowStart = windowEnd - windowYears * 12;

  const ranges: Array<{ from: number; to: number }> = [];
  if (Array.isArray(entries)) {
    for (const e of entries) {
      const from = monthIndex((e as HistoryEntry | null)?.date_from, 'from');
      let to = monthIndex((e as HistoryEntry | null)?.date_to, 'to');
      if (from == null || to == null) continue;
      if (to > windowEnd) to = windowEnd; // ongoing residence
      if (to < from) continue;
      if (to < windowStart || from > windowEnd) continue; // outside the window
      ranges.push({ from: Math.max(from, windowStart), to });
    }
  }

  if (ranges.length === 0) {
    return {
      complete: false,
      usedEntries: 0,
      gaps: [{ from: label(windowStart), to: label(windowEnd) }],
    };
  }

  ranges.sort((a, b) => a.from - b.from);
  const merged: Array<{ from: number; to: number }> = [ranges[0]];
  for (const r of ranges.slice(1)) {
    const last = merged[merged.length - 1];
    if (r.from <= last.to + 1) {
      last.to = Math.max(last.to, r.to);
    } else {
      merged.push({ ...r });
    }
  }

  const gaps: CoverageGap[] = [];
  if (merged[0].from > windowStart) {
    gaps.push({ from: label(windowStart), to: label(merged[0].from - 1) });
  }
  for (let i = 1; i < merged.length; i++) {
    gaps.push({ from: label(merged[i - 1].to + 1), to: label(merged[i].from - 1) });
  }
  if (merged[merged.length - 1].to < windowEnd) {
    gaps.push({ from: label(merged[merged.length - 1].to + 1), to: label(windowEnd) });
  }

  return { complete: gaps.length === 0, gaps, usedEntries: ranges.length };
}

/** "Mar 2019 to Nov 2019, May 2021 to Aug 2021" */
export function formatGaps(gaps: CoverageGap[]): string {
  return gaps.map((g) => (g.from === g.to ? g.from : `${g.from} to ${g.to}`)).join(', ');
}
